import PropTypes from "prop-types";
import {useState} from "react";
import useCustomEventListener from "@/hooks/useCustomEventListener";
import useSubscriptionTag from "@/hooks/useSubscriptionTag";
import { formatMoney, formatMoneyKg, formatMoneyL } from "@/utils/formatMoney";
import {useTranslation} from "react-i18next";
import {getCurrentRegion, NON_SUBSCRIPTION_REGIONS} from "@/utils/regions";

const ProductCardPrice = ({
  defaultVariantId,
  defaultSellingPlanId,
  prices,
  sectionId,
  enableTag = false,
}) => {
  const {t} = useTranslation();
  
  // For non-subscription regions, ignore the defaultSellingPlanId
  const region = getCurrentRegion();
  const isNonSubscriptionRegion = region && NON_SUBSCRIPTION_REGIONS.includes(region);
  const initialSellingPlanId = isNonSubscriptionRegion ? null : defaultSellingPlanId;
  
  const [wasOpened, setWasOpened] = useState(false);
  const [id, setId] = useState(defaultVariantId);
  const [quantity, setQuantity] = useState(1);
  const [sellingPlanId, setSellingPlanId] = useState(initialSellingPlanId);

  const handleVariantChange = payload => {
    setId(payload.variantId);
    setQuantity(payload.quantity);
    // For non-subscription regions, always use null for selling_plan
    setSellingPlanId(isNonSubscriptionRegion ? null : payload.selling_plan);
  };

  const handleOpenChange = () => {
    setWasOpened(true);
  };
  const handleCloseChange = () => {
    setWasOpened(false);
  };

  const getPrice = (id, quantity, sellingPlanId) =>
    prices.find(
      priceData =>
        priceData.id === id &&
        priceData.quantity === quantity &&
        priceData.selling_plan === sellingPlanId,
    ) ??
    prices.find(
      priceData =>
        priceData.id === id && priceData.selling_plan === sellingPlanId,
    ) ??
    prices.find(
      priceData => priceData.id === id && priceData.quantity === quantity,
    ) ??
    prices.find(priceData => priceData.id === id) ??
    prices[0];

  const selectedPrice = getPrice(id, quantity, sellingPlanId);

  const {subscriptionTagLabel} = useSubscriptionTag(selectedPrice);

  useCustomEventListener(
    `product-form:change:${defaultVariantId}-${sectionId}`,
    handleVariantChange,
  );

  useCustomEventListener(
    `product-form:open:${defaultVariantId}-${sectionId}`,
    handleOpenChange,
  );
  useCustomEventListener(
    `product-form:close:${defaultVariantId}-${sectionId}`,
    handleCloseChange,
  );

  return (
    <div className="product-card-price flex flex-wrap gap-1 whitespace-nowrap sm:block md:text-right gap-x-1 mt-[0px] leading-4">
      <span className="font-dm-sans text-base font-normal not-italic leading-[140%] tracking-[0.00875rem] text-deep-green md:tracking-normal">
        {formatMoney(selectedPrice.price)}
      </span>
      {!wasOpened ? (
        <></>
      ) : (
        selectedPrice.compareAtPrice && selectedPrice.compareAtPrice > selectedPrice.price && (
          <span className="font-dm-sans text-lg font-normal not-italic leading-[140%] tracking-[0.00875rem] text-deep-green line-through decoration-1 opacity-50 md:tracking-normal">
            {formatMoney(selectedPrice.compareAtPrice)}
          </span>
        )
      )}
      {enableTag && subscriptionTagLabel && (
        <span className="mobile-tag tag tag--secondary relative flex whitespace-nowrap px-[0.38rem] py-1">
          {t("product_card.subscription_tag", {
            percentage: subscriptionTagLabel,
          })}
        </span>
      )}
      {(selectedPrice.kg_price || selectedPrice.l_price) && (
        <div className="col-span-2 sm:col-span-1 lg:text-right">
          <span className="italic opacity-50 text-xs">
            {selectedPrice.kg_price ? formatMoneyKg(selectedPrice.kg_price) : formatMoneyL(selectedPrice.l_price)}
          </span>
        </div>
      )}
    </div>
  );
};

ProductCardPrice.propTypes = {
  sectionId: PropTypes.string,
  defaultVariantId: PropTypes.number,
  defaultSellingPlanId: PropTypes.number,
  prices: PropTypes.array,
  enableTag: PropTypes.bool,
};

export default ProductCardPrice;
